/**
 * LoginPage - Authentication form with neon input effects
 * 
 * Login form with neon input glow effects, social login options,
 * and password recovery link. Features glassmorphism card design,
 * form validation, and animated interactions.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores';
import { Heading, Text, Stack, Container, Divider } from '@/components/ui';
import styles from './LoginPage.module.css';

// Social Login Icons as SVG components
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" className={styles.socialIcon}>
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" className={styles.socialIcon}>
      <path
        fill="currentColor"
        d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}

// Password Input Component with show/hide toggle
function PasswordInput({ value, onChange, error, placeholder, disabled }) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={styles.inputWrapper}>
      <div className={styles.inputIcon}>
        <Lock size={20} />
      </div>
      <input
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? 'password-error' : undefined}
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className={styles.passwordToggle}
        disabled={disabled}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

// Social Login Button Component
function SocialLoginButton({ provider, icon: Icon, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={styles.socialButton}
      aria-label={`Sign in with ${provider}`}
    >
      <Icon />
      <span>Continue with {provider}</span>
    </button>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading: authLoading, error: authError } = useAuthStore();

  // Form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Trigger fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    const success = await login(formData.email, formData.password);
    setIsLoading(false);

    if (success) {
      navigate('/discover');
    }
  };

  // Handle input changes
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle social login (mock)
  const handleSocialLogin = (provider) => {
    console.log(`Login with ${provider}`);
    // In a real app, this would trigger OAuth flow
  };

  const isSubmitting = isLoading || authLoading;

  return (
    <div className={styles.page}>
      <Container size="sm" padding className={styles.container}>
        {/* Back Button */}
        <Link to="/welcome" className={styles.backLink} aria-label="Go back">
          <ArrowLeft size={24} />
        </Link>

        {/* Glass Card */}
        <div
          className={`${styles.card} ${isVisible ? styles.cardVisible : ''} ${
            Object.keys(errors).length > 0 && isSubmitting ? styles.cardShake : ''
          }`}
        >
          <Stack direction="column" gap={8} align="center">
            {/* Logo & Header */}
            <div className={styles.header}>
              <div className={styles.logo}>
                <span className={styles.logoIcon}>⚡</span>
              </div>
              <Heading level={1} size="h2" align="center" className={styles.title}>
                Welcome Back
              </Heading>
              <Text size="base" color="secondary" align="center">
                Sign in to continue your athletic journey
              </Text>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <Stack direction="column" gap={5} fullWidth>
                {/* Email Field */}
                <div className={styles.field}>
                  <label htmlFor="email" className={styles.label}>
                    Email
                  </label>
                  <div className={styles.inputWrapper}>
                    <div className={styles.inputIcon}>
                      <Mail size={20} />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      placeholder="athlete@sportspots.com"
                      disabled={isSubmitting}
                      className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                      aria-invalid={errors.email ? 'true' : 'false'}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  </div>
                  {errors.email && (
                    <span id="email-error" className={styles.errorText} role="alert">
                      {errors.email}
                    </span>
                  )}
                </div>

                {/* Password Field */}
                <div className={styles.field}>
                  <div className={styles.labelRow}>
                    <label htmlFor="password" className={styles.label}>
                      Password
                    </label>
                    <Link to="/forgot-password" className={styles.forgotLink}>
                      Forgot Password?
                    </Link>
                  </div>
                  <PasswordInput
                    value={formData.password}
                    onChange={handleChange('password')}
                    placeholder="Enter your password"
                    disabled={isSubmitting}
                    error={errors.password}
                  />
                  {errors.password && (
                    <span id="password-error" className={styles.errorText} role="alert">
                      {errors.password}
                    </span>
                  )}
                </div>

                {/* Submit Error */}
                {authError && (
                  <div className={styles.authError} role="alert">
                    {authError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.submitButton}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className={styles.spinner} />
                      <span>Signing In...</span>
                    </>
                  ) : (
                    <span>Sign In</span>
                  )}
                </button>
              </Stack>
            </form>

            {/* Divider */}
            <Divider label="or continue with" color="muted" className={styles.divider} />

            {/* Social Login */}
            <Stack direction="column" gap={3} fullWidth>
              <SocialLoginButton
                provider="Google"
                icon={GoogleIcon}
                onClick={() => handleSocialLogin('Google')}
                disabled={isSubmitting}
              />
              <SocialLoginButton
                provider="Apple"
                icon={AppleIcon}
                onClick={() => handleSocialLogin('Apple')}
                disabled={isSubmitting}
              />
            </Stack>

            {/* Footer */}
            <div className={styles.footer}>
              <Text size="sm" color="secondary" align="center">
                Don&apos;t have an account?{' '}
                <Link to="/signup" className={styles.signupLink}>
                  Sign Up
                </Link>
              </Text>
            </div>
          </Stack>
        </div>
      </Container>
    </div>
  );
}

export default LoginPage;